import React from "react";
import { BrowserRouter as Router, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import NotFound from "./pages/NotFound";
import { useAuth } from "./contexts/AuthContext";
import FbBulkScheduleNotifier from "./components/notifications/FbBulkScheduleNotifier";

// Public pages
import Home from "./pages/Home";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import Contact from "./pages/Contact";
import DataDeletion from "./pages/DataDeletion";

// Existing pages
import WordPressContentMonitor from "./pages/word-press-content-monitor";
import PinterestAnalyticsHub from "./pages/pinterest-analytics-hub";
import AutomationControlPanel from "./pages/automation-control-panel";
import FacebookEngagementCenter from "./pages/facebook-engagement-center";
import SocialMediaOverview from "./pages/social-media-overview";

// Auth pages
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import Profile from "./pages/Profile";
import FacebookCallback from "./pages/auth/facebook/callback";

// WordPress pages
import BlogPosts from "./pages/wordpress/BlogPosts";
import AddBlogPost from "./pages/wordpress/AddBlogPost";
import Sites from "./pages/wordpress/Sites";
import AddSite from "./pages/wordpress/AddSite";
import CopyBlogs from "./pages/wordpress/CopyBlogs";

// Facebook pages
import PostsPlanner from "./pages/facebook/PostsPlanner";
import PageStats from "./pages/facebook/PageStats";
import ThreadsAccounts from "./pages/facebook/ThreadsAccounts";
import CreateThread from "./pages/threads/CreateThread";
import InstagramAccounts from "./pages/instagram/InstagramAccounts";

// Pinterest pages
import Accounts from "./pages/pinterest/Accounts";
import PinLibrary from "./pages/pinterest/PinLibrary";
import CreatePin from "./pages/pinterest/CreatePin";
import Statistics from "./pages/pinterest/Statistics";
import PinterestAudit from "./pages/pinterest/PinterestAudit";
import PerformanceAnalytics from "./pages/pinterest/PerformanceAnalytics";
import RecoveryCenter from "./pages/pinterest/RecoveryCenter";
import PinAutomation from "./pages/pinterest/PinAutomation";

// Tools pages
import Prompts from "./pages/tools/Prompts";
import AddPrompt from "./pages/tools/AddPrompt";
import EditPrompt from "./pages/tools/EditPrompt";
import Settings from "./pages/tools/Settings";

import UsersOverview from "./pages/admin/UsersOverview";
import UserDetails from "./pages/admin/UserDetails";

// Google Site pages
import ComingSoon from "./pages/ComingSoon";

// Etsy pages
import EtsyAccounts from "./pages/etsy/EtsyAccounts";
import DigitalProducts from "./pages/etsy/DigitalProducts";

// Pattern Studio pages
import PatternStudioDashboard from "./pages/etsy/pattern-studio";
import PatternStudioBuilder from "./pages/etsy/pattern-studio/PatternStudioLayout";

// Mock data for components requiring props

// Add mock data for ComingSoon component
const comingSoonDefaultProps = {
  title: "Coming Soon",
  description: "This feature is currently under development and will be available soon."
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const ManagerRoute = ({ children }) => {
  const { user, isManager, loading, profileLoading } = useAuth();
  if (loading || profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (!user || !isManager) {
    return <Navigate to="/automation-control-panel" replace />;
  }
  return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/automation-control-panel" replace />;
  }
  
  return children;
};

const Routes = () => {
  return (
    <Router>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><SignIn /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><SignUp /></PublicRoute>} />

          <Route path="/" element={<Home />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/data-deletion" element={<DataDeletion />} />

          <Route path="/signin" element={<Navigate to="/login" replace />} />
          <Route path="/signup" element={<Navigate to="/register" replace />} />
          <Route path="/logout" element={<Navigate to="/login" replace />} />

          {/* Facebook OAuth Callback - No auth required, handles its own session */}
          <Route path="/auth/facebook/callback" element={<FacebookCallback />} />

          {/* Legacy redirect — old home URL now points to the new dashboard */}
          <Route path="/social-media-overview" element={<Navigate to="/automation-control-panel" replace />} />

          {/* Protected Routes */}
          <Route 
            path="/word-press-content-monitor" 
            element={<ProtectedRoute><WordPressContentMonitor /></ProtectedRoute>} 
          />
          <Route 
            path="/pinterest-analytics-hub" 
            element={<ProtectedRoute><PinterestAnalyticsHub /></ProtectedRoute>} 
          />
          <Route 
            path="/automation-control-panel" 
            element={<ProtectedRoute><AutomationControlPanel /></ProtectedRoute>} 
          />
          <Route 
            path="/facebook-engagement-center" 
            element={<ProtectedRoute><FacebookEngagementCenter /></ProtectedRoute>} 
          />
          <Route 
            path="/profile" 
            element={<ProtectedRoute><Profile /></ProtectedRoute>} 
          />

          {/* WordPress Routes */}
          <Route 
            path="/wp/posts" 
            element={<ProtectedRoute><BlogPosts /></ProtectedRoute>} 
          />
          <Route 
            path="/wp/add-post" 
            element={<ProtectedRoute><AddBlogPost /></ProtectedRoute>} 
          />
          <Route 
            path="/wp/sites" 
            element={<ProtectedRoute><Sites /></ProtectedRoute>} 
          />
          <Route 
            path="/wp/add-site" 
            element={<ProtectedRoute><AddSite /></ProtectedRoute>} 
          />
          <Route 
            path="/wp/copy-blogs" 
            element={<ProtectedRoute><CopyBlogs /></ProtectedRoute>} 
          />

          {/* Facebook Routes */}
          <Route
            path="/fb/page-stats"
            element={<ProtectedRoute><PageStats /></ProtectedRoute>}
          />
          <Route
            path="/fb/posts-planner"
            element={<ProtectedRoute><PostsPlanner /></ProtectedRoute>}
          />
          <Route
            path="/fb/threads-accounts"
            element={<Navigate to="/threads/accounts" replace />}
          />

          {/* Threads Routes */}
          <Route
            path="/threads/accounts"
            element={<ProtectedRoute><ThreadsAccounts /></ProtectedRoute>}
          />
          <Route
            path="/threads/instagram-accounts"
            element={<ProtectedRoute><InstagramAccounts /></ProtectedRoute>}
          />
          <Route
            path="/threads/create"
            element={<ProtectedRoute><CreateThread /></ProtectedRoute>}
          />

          {/* Pinterest Routes */}
          <Route 
            path="/pin/accounts" 
            element={<ProtectedRoute><Accounts /></ProtectedRoute>} 
          />
          <Route 
            path="/pin/library" 
            element={<ProtectedRoute><PinLibrary /></ProtectedRoute>} 
          />
          <Route
            path="/pin/create"
            element={<ProtectedRoute><CreatePin /></ProtectedRoute>}
          />
          <Route path="/pin/recovery" element={<ProtectedRoute><RecoveryCenter /></ProtectedRoute>} />
          <Route path="/pin/performance-analytics" element={<ProtectedRoute><PerformanceAnalytics /></ProtectedRoute>} />
          <Route
            path="/pin/trend-keywords"
            element={<ProtectedRoute><PinterestAudit /></ProtectedRoute>}
          />
          <Route
            path="/pin/stats"
            element={<ProtectedRoute><Statistics /></ProtectedRoute>}
          />
          <Route 
            path="/pin/trending-keywords" 
            element={<ProtectedRoute><PinterestAudit /></ProtectedRoute>} 
          />
          <Route 
            path="/pin/automation" 
            element={<ProtectedRoute><PinAutomation /></ProtectedRoute>} 
          />

          {/* Etsy Routes */}
          <Route
            path="/etsy/accounts"
            element={<ProtectedRoute><EtsyAccounts /></ProtectedRoute>}
          />
          <Route
            path="/etsy/digital-products"
            element={<ProtectedRoute><DigitalProducts /></ProtectedRoute>}
          />

          {/* Pattern Studio Routes */}
          <Route
            path="/etsy/pattern-studio"
            element={<ProtectedRoute><PatternStudioDashboard /></ProtectedRoute>}
          />
          <Route
            path="/etsy/pattern-studio/new"
            element={<ProtectedRoute><PatternStudioBuilder /></ProtectedRoute>}
          />
          <Route
            path="/etsy/pattern-studio/:id/edit"
            element={<ProtectedRoute><PatternStudioBuilder /></ProtectedRoute>}
          />
          <Route
            path="/etsy/pattern-studio/:id/preview"
            element={<ProtectedRoute><PatternStudioBuilder /></ProtectedRoute>}
          />

          {/* Google Site Routes - Add after Pinterest routes */}
          <Route
            path="/google-site/pages-manager"
            element={<ProtectedRoute><ComingSoon {...comingSoonDefaultProps} /></ProtectedRoute>}
          />
          <Route
            path="/google-site/add-page"
            element={<ProtectedRoute><ComingSoon {...comingSoonDefaultProps} /></ProtectedRoute>}
          />
          <Route 
            path="/google-site/templates" 
            element={<ProtectedRoute><ComingSoon {...comingSoonDefaultProps} /></ProtectedRoute>} 
          />
          <Route 
            path="/google-site/auto-poster" 
            element={<ProtectedRoute><ComingSoon {...comingSoonDefaultProps} /></ProtectedRoute>} 
          />
          <Route 
            path="/google-site/bulk-update" 
            element={<ProtectedRoute><ComingSoon {...comingSoonDefaultProps} /></ProtectedRoute>} 
          />

          {/* Tools Routes */}
          <Route 
            path="/tools/prompts" 
            element={<ProtectedRoute><Prompts /></ProtectedRoute>} 
          />
          <Route 
            path="/tools/add-prompt" 
            element={<ProtectedRoute><AddPrompt /></ProtectedRoute>} 
          />
          <Route 
            path="/tools/edit-prompt/:id" 
            element={<ProtectedRoute><EditPrompt /></ProtectedRoute>} 
          />
          <Route 
            path="/tools/settings" 
            element={<ProtectedRoute><Settings /></ProtectedRoute>} 
          />

          {/* Manager-only User Management */}
          <Route 
            path="/admin/users"
            element={<ManagerRoute><UsersOverview /></ManagerRoute>}
          />
          <Route 
            path="/admin/users/:id"
            element={<ManagerRoute><UserDetails /></ManagerRoute>}
          />
          
          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
        <FbBulkScheduleNotifier />
      </ErrorBoundary>
    </Router>
  );
};

export default Routes;
